import { useContext, useEffect, useState } from 'react';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { ConversationCallTranscriptResponse, getConversationCallTranscript } from '../../../../domain/use_cases/conversations/getConversationCallTranscript';

// Same as useConversationCallRecordingController, loaded once the transcript is opened
const useConversationCallTranscriptController = (subscriptionId: string, callId: string, isEnabled: boolean) => {
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany } = useContext(CompanyContext);

  const [transcript, setTranscript] = useState<{ callId: string; response: ConversationCallTranscriptResponse } | null>(null);

  const [failedCallId, setFailedCallId] = useState<string | null>(null);

  const companyId = activeCompany?.id;
  const isLoaded = transcript?.callId === callId;
  const hasFailed = failedCallId === callId;

  useEffect(() => {
    if (!isEnabled || isLoaded || hasFailed || !companyId || !authentication) return;

    let isCancelled = false;
    getConversationCallTranscript({ company_id: companyId, subscription_id: subscriptionId, call_id: callId }, authentication)
      .then((response) => {
        if (!isCancelled) setTranscript({ callId, response });
      })
      .catch(() => {
        if (!isCancelled) setFailedCallId(callId);
      });
    return () => {
      isCancelled = true;
    };
  }, [isEnabled, isLoaded, hasFailed, subscriptionId, callId, companyId, authentication]);

  // For the download, which does not wait for the transcript to be opened
  const loadTranscript = async (): Promise<ConversationCallTranscriptResponse | null> => {
    if (isLoaded) return transcript.response;
    if (!companyId || !authentication) return null;
    const response = await getConversationCallTranscript(
      { company_id: companyId, subscription_id: subscriptionId, call_id: callId },
      authentication,
    );
    setTranscript({ callId, response });
    return response;
  };

  return {
    actions: { loadTranscript },
    state: {
      transcript: isLoaded ? transcript.response : null,
      isLoading: isEnabled && !isLoaded && !hasFailed,
      hasFailed,
    },
  };
};

export default useConversationCallTranscriptController;
